/**
 * Kontosperre — Sperrzeitpunkt lesen und als Restzeit ausdruecken.
 *
 * Nach fuenf Fehlversuchen sperrt das Backend ein Konto fuer 15 Minuten und antwortet
 * mit HTTP 423 statt 401 — damit eine Sperre nicht als „falsches Passwort" erscheint.
 * Der Body traegt den Sperrzeitpunkt:
 *
 *   { "message": "Konto ist gesperrt. Bitte später erneut versuchen.",
 *     "lockedUntil": "2026-09-01T22:20:26.5443698Z" }
 *
 * Bis 2026-09-02 hat die PWA nur `message` angezeigt. Der Trainer erfuhr dadurch nicht,
 * wie lange er warten muss — und probierte weiter, was die Sperre nicht verlaengert,
 * aber auch nicht hilft.
 */

import axios from 'axios'

/**
 * Liest den Sperrzeitpunkt aus einer 423-Antwort. `null` fuer jede andere Antwort.
 */
export function leseSperrzeitpunkt(fehler: unknown): Date | null {
  if (!axios.isAxiosError(fehler)) return null
  if (fehler.response?.status !== 423) return null

  const rohwert = (fehler.response.data as { lockedUntil?: unknown } | undefined)?.lockedUntil
  if (typeof rohwert !== 'string' || rohwert.length === 0) return null

  const zeitpunkt = new Date(alsUtcWennOhneZone(rohwert))
  return Number.isNaN(zeitpunkt.getTime()) ? null : zeitpunkt
}

/**
 * Ergaenzt ein fehlendes Zonenkennzeichen um „Z“.
 *
 * Das Backend kennzeichnet ausdruecklich als UTC (`DateTime.SpecifyKind`, AuthController),
 * weil SQLite den `Kind` nicht speichert. Faellt das je weg, wuerde JavaScript den Wert als
 * Ortszeit lesen — im Berliner Sommer zwei Stunden zu frueh, der Countdown waere sofort
 * abgelaufen. Genau diese Verwechslung hat in diesem Projekt schon zweimal Zeit gekostet.
 */
function alsUtcWennOhneZone(wert: string): string {
  const hatZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(wert)
  return hatZone ? wert : `${wert}Z`
}

/**
 * Verbleibende Sperrzeit als Satzteil („noch 14 Minuten"), oder `null`, wenn die Sperre
 * abgelaufen ist. Unter einer Minute wird in Sekunden gezaehlt, damit der Countdown am
 * Ende nicht auf „noch 1 Minute" stehen bleibt.
 *
 * Angebrochene Einheiten werden aufgerundet: bei 14:01 verbleibend ist „noch 15 Minuten"
 * richtig und „noch 14 Minuten" eine Zusage, die das Backend nicht einhaelt.
 */
export function formatiereRestzeit(sperrzeitpunkt: Date, jetzt: Date): string | null {
  const restMillisekunden = sperrzeitpunkt.getTime() - jetzt.getTime()
  if (Number.isNaN(restMillisekunden) || restMillisekunden <= 0) return null

  const restSekunden = Math.ceil(restMillisekunden / 1000)

  if (restSekunden < 60) {
    return `noch ${restSekunden} ${restSekunden === 1 ? 'Sekunde' : 'Sekunden'}`
  }

  const restMinuten = Math.ceil(restSekunden / 60)
  return `noch ${restMinuten} ${restMinuten === 1 ? 'Minute' : 'Minuten'}`
}
